import React, { useState, useEffect, useRef, useMemo } from 'react';
import { productAPI, orderAPI, dealerAPI, analyticsAPI, exportAPI, transporterAPI, crmAPI, stockAPI, productionAPI, getFileUrl } from '../../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { LayoutDashboard, Package, ShoppingBag, Users, LogOut, Plus, Edit, Trash2, CheckCircle, Truck, Loader2, FileText, BarChart3, PackageCheck, DollarSign, Download, IndianRupee, Upload, ImageIcon, ArrowUpDown, Eye, CalendarClock, Pencil, ClipboardList, Undo2, Menu, X, UserRound, MessageCircle, ArrowLeft, Warehouse, Minus, Factory, ChevronRight, ChevronDown, Calendar as CalendarIcon, AlertTriangle } from 'lucide-react';
import { DeliveryView } from './DeliveryView';
import { useWindowedList } from '../../hooks/useWindowedList';

// ============ STOCK TAB ============
// Helper: format ISO date YYYY-MM-DD relative to today.
const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (iso, n) => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export const StockTab = ({ products, orders = [], transporters = [], fetchData, refreshKey = 0, userRole, onNavigateToProduction }) => {
  // Manager role is read-only for stock corrections — only admins can fire
  // the negative-stock auto-fix or open the manual Adjust dialog. Default to
  // false when the prop is missing so we fail closed.
  const isAdmin = (userRole || '').toLowerCase() === 'admin';
  // Local review dialog for the dispatch popover — keeps StockTab independent
  // of OrdersTab. Read-only view of order metadata + items, no actions.
  const [reviewOrderId, setReviewOrderId] = useState(null);
  const reviewOrder = React.useMemo(() => {
    if (!reviewOrderId) return null;
    return (orders || []).find((o) => o.id === reviewOrderId) || null;
  }, [reviewOrderId, orders]);
  const [stockSubTab, setStockSubTab] = useState('delivery'); // 'stock' | 'delivery'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Click-to-sort for the Stock table's Product and Opening Stock columns.
  // key: 'product' | 'opening' | null (null = default alpha-by-product with
  // virtuals grouped below their parent). dir cycles null → asc → desc → null.
  const [stockSort, setStockSort] = useState({ key: null, dir: null });
  const cycleStockSort = (key) => {
    setStockSort((p) => {
      if (p.key !== key) return { key, dir: 'asc' };
      if (p.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: null };
    });
  };
  const [adjust, setAdjust] = useState(null); // { product_id, product_name, facility, current }
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // In-transit audit dialog — shows the dispatches contributing to the
  // `in_transit` count vs what the Delivery view's active list will display.
  // Useful when the two numbers feel out of sync.
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const openAudit = async () => {
    setAuditOpen(true);
    setAuditLoading(true);
    try {
      const res = await analyticsAPI.getInTransitAudit();
      setAuditData(res.data);
    } catch (e) {
      toast.error('Failed to load in-transit audit');
      setAuditData(null);
    } finally { setAuditLoading(false); }
  };
  // Date range selector for the "daily detail" view.
  // datePreset: 'today' | 'tomorrow' | 'custom'
  // Always defaults to TODAY on every entry to the tab (user spec 18/Jun/2026).
  // Operator can switch to Custom range mid-session; that change does NOT
  // persist across tab navigations — every fresh entry returns to Today.
  const [datePreset, setDatePreset] = useState('today');
  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  // Expanded product rows — persisted in localStorage so it survives date switches.
  const [expanded, setExpanded] = useState(() => {
    try {
      const raw = localStorage.getItem('vikas:stockExpandedProducts');
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem('vikas:stockExpandedProducts', JSON.stringify(expanded)); } catch {}
  }, [expanded]);

  // 21/Feb/2026: Virtual products (auto-spawned for MISC orders / sub-brand
  // production rows) appear as their own top-level row by default. When the
  // operator toggles "Group by parent" ON, virtual products fold under their
  // semantic parent (parent row sums Opening/Production/Dispatch/Physical
  // /Available across self + all virtual children; expand chevron reveals
  // children with their own per-row figures). Persists across sessions.
  const [groupByParent, setGroupByParent] = useState(() => {
    try { return localStorage.getItem('vikas:stockGroupByParent') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('vikas:stockGroupByParent', groupByParent ? '1' : '0'); } catch {}
  }, [groupByParent]);

  // Resolve preset → from/to dates. Always triggers a reload (even when the
  // preset's dates equal the current state) so operators get a clear "data
  // refreshed" beat when they tap Today/Tomorrow — previously clicking Today
  // while already on today was a silent no-op because React skipped the
  // setState and the [dateFrom, dateTo] effect never fired.
  const applyPreset = (preset) => {
    setDatePreset(preset);
    let nextFrom = dateFrom;
    let nextTo = dateTo;
    if (preset === 'today') {
      nextFrom = nextTo = todayISO();
    } else if (preset === 'tomorrow') {
      nextFrom = nextTo = addDaysISO(todayISO(), 1);
    }
    if (nextFrom !== dateFrom) setDateFrom(nextFrom);
    if (nextTo !== dateTo) setDateTo(nextTo);
    // Force a reload when dates didn't change (React would otherwise skip
    // the effect). Same-date click → explicit load() call.
    if (nextFrom === dateFrom && nextTo === dateTo) {
      load();
    }
    // 'custom' keeps whatever dates are currently set; the user edits them inline.
  };

  // Monotonic request counter — prevents an EARLIER load() (e.g. fired for
  // an interim date state while the operator was still typing the range)
  // from overwriting the LATEST response. Without this, race conditions
  // between concurrent fetches produced the "data briefly correct, then
  // inflates to out-of-range numbers" bug reported 19/Jun/2026. Symptom:
  // a stale wider-range response arrives after the narrow-range one and
  // replaces it.
  const loadSeqRef = React.useRef(0);

  const load = async () => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    try {
      const res = await stockAPI.getDaily(dateFrom, dateTo);
      // Ignore stale responses — only the most recent request gets to set state.
      if (seq !== loadSeqRef.current) return;
      setData(res.data);
    } catch (e) {
      if (seq === loadSeqRef.current) toast.error('Failed to load stock');
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo]);

  // Silent refresh when parent bumps `refreshKey` (on tab re-entry). Skips
  // initial render — `load()` runs from the [dateFrom, dateTo] effect on mount.
  // Also re-fetches global orders/products via the `fetchData` prop so the
  // Delivery sub-view sees up-to-date dispatch/order context, and bumps
  // `deliveryRefreshKey` so DeliveryView re-fetches its `/api/dispatches`
  // payload. Local UI state (expanded rows, search input, date filters,
  // sub-tab selection) is left intact.
  const isFirstStockRefreshRef = React.useRef(true);
  const [deliveryRefreshKey, setDeliveryRefreshKey] = useState(0);
  useEffect(() => {
    if (isFirstStockRefreshRef.current) { isFirstStockRefreshRef.current = false; return; }
    load();
    if (typeof fetchData === 'function') fetchData();
    setDeliveryRefreshKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const toggleExpand = (pid) => {
    setExpanded((prev) => ({ ...prev, [pid]: !prev[pid] }));
  };

  // Excel / PDF download for the selected date range. Uses blob → object URL
  // so the browser triggers a real save-as dialog with the right filename.
  const [downloading, setDownloading] = useState(false);
  const downloadStock = async (kind) => {
    setDownloading(true);
    try {
      const fn = kind === 'pdf' ? exportAPI.stockPDF : exportAPI.stockExcel;
      const res = await fn({ date_from: dateFrom, date_to: dateTo });
      const blob = new Blob([res.data], {
        type: kind === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const suffix = dateFrom === dateTo ? dateFrom : `${dateFrom}_${dateTo}`;
      a.download = `stock_${suffix}.${kind === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Stock ${kind.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(`Download failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setDownloading(false); }
  };

  const openAdjust = (row, facility) => {
    setAdjust({
      product_id: row.product_id,
      product_name: row.product_name,
      facility,
      current: row.by_facility[facility] || 0,
    });
    setDelta('');
    setReason('');
  };

  // One-click "Fix" for the negative-opening-stock strip. Applies the exact
  // corrective adjustment (delta = -qty, where qty is the negative opening)
  // directly without showing the manual Adjust dialog. Tagged with a clear
  // reason so the History view shows the auto-correction trail.
  const [fixingKey, setFixingKey] = useState(null);
  // Tracks (product × facility) keys whose Fix was just applied — used to
  // drop them from the strip even when the backend's opening recompute still
  // shows negative (manual adjustments dated NOW don't shift the opening for
  // an in-range view because they're counted in `range_adj`, not pre-range).
  // The dismissal is per-load: the next `load()` call rebuilds data and we
  // re-evaluate from scratch.
  const [dismissedAlerts, setDismissedAlerts] = useState(() => new Set());
  // Patch a single row in `data.rows` after a stock adjustment so the UI
  // reflects the change instantly — full /stock/daily refetch still runs
  // in background (via load()) for derived aggregates. Targets only the
  // row + facility actually adjusted; everything else is left untouched.
  const patchAdjustment = (productId, facility, delta) => {
    if (!productId || !facility || !delta) return;
    setData(prev => {
      if (!prev || !prev.rows) return prev;
      const rows = prev.rows.map(r => {
        if (r.product_id !== productId) return r;
        const byFac = { ...(r.by_facility || {}) };
        byFac[facility] = (byFac[facility] || 0) + delta;
        const closingByFac = { ...(r.closing_by_facility || {}) };
        closingByFac[facility] = (closingByFac[facility] || 0) + delta;
        const adjByFac = { ...(r.adjustment_in_range_by_facility || {}) };
        adjByFac[facility] = (adjByFac[facility] || 0) + delta;
        return {
          ...r,
          by_facility: byFac,
          closing_by_facility: closingByFac,
          total: (r.total || 0) + delta,
          closing_total: (r.closing_total || 0) + delta,
          adjustment_in_range_by_facility: adjByFac,
          adjustment_in_range_total: (r.adjustment_in_range_total || 0) + delta,
        };
      });
      return { ...prev, rows };
    });
  };

  const fixNegativeOpening = async (row, facility, qty) => {
    const delta = -Number(qty);
    if (!delta || Number.isNaN(delta)) return;
    const key = `${row.product_id}|${facility}`;
    setFixingKey(key);
    try {
      await stockAPI.adjust(
        facility,
        row.product_id,
        delta,
        `Auto-fix negative opening stock (+${delta} bags)`
      );
      toast.success(`Fixed ${row.product_name} at ${facility}: +${delta} bags`);
      setDismissedAlerts((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      patchAdjustment(row.product_id, facility, delta);  // instant UI reflect
      load();                                            // background reconcile
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Adjustment failed');
    } finally {
      setFixingKey(null);
    }
  };
  // Clear dismissals when the date range changes — the operator is looking
  // at a different window, so prior dismissals don't apply.
  useEffect(() => { setDismissedAlerts(new Set()); }, [dateFrom, dateTo]);

  const submitAdjust = async () => {
    const n = parseInt(delta, 10);
    if (!n || Number.isNaN(n)) { toast.error('Enter a non-zero quantity (use minus for deduction)'); return; }
    setSaving(true);
    try {
      await stockAPI.adjust(adjust.facility, adjust.product_id, n, reason || null);
      toast.success(`Stock updated: ${adjust.product_name} at ${adjust.facility}`);
      patchAdjustment(adjust.product_id, adjust.facility, n);  // instant UI reflect
      setAdjust(null);
      load();                                                  // background reconcile
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Adjustment failed');
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const res = await stockAPI.history({ limit: 100 });
      setHistory(res.data);
    } catch { toast.error('Failed to load history'); }
    finally { setHistoryLoading(false); }
  };

  const fmtDateTime = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return '-'; }
  };

  // Base filter: search by name + hide truly inactive virtual products.
  // A virtual is hidden ONLY when EVERY signal is zero — current stock,
  // in-transit, AND any production/dispatch/adjustment activity inside the
  // selected date window. This way a virtual that was produced and fully
  // sold in the window still shows its movements (Production + Dispatch
  // columns) instead of vanishing because the closing total happens to be 0.
  // Negative stock always stays visible — it's a red flag.
  const baseRows = (data?.rows || []).filter(r => {
    if (search && !(r.product_name || '').toLowerCase().includes(search.toLowerCase())) return false;
    if (r.is_virtual) {
      const hasActivity = (r.total || 0) !== 0
        || (r.in_transit || 0) !== 0
        || (r.production_in_range_total || 0) !== 0
        || (r.dispatch_in_range_total || 0) !== 0
        || (r.adjustment_in_range_total || 0) !== 0;
      if (!hasActivity) return false;
    }
    return true;
  });

  // Group-by-parent transformation: when ON, each parent main product's row
  // sums in its virtual children (Opening / Production / Dispatch / In-Transit
  // / Physical / Available all rolled up). Virtual children are not shown
  // at the top level — they appear inside the expanded panel of their
  // parent. When OFF, virtual products are top-level rows (alongside main
  // products) and the parent shows only its own numbers.
  const filteredRows = (() => {
    if (!groupByParent) return baseRows;
    const byParent = {};
    baseRows.forEach(r => {
      if (r.is_virtual && r.parent_product_id) {
        (byParent[r.parent_product_id] = byParent[r.parent_product_id] || []).push(r);
      }
    });
    const childIds = new Set();
    Object.values(byParent).forEach(arr => arr.forEach(c => childIds.add(c.product_id)));
    const summed = baseRows
      .filter(r => !childIds.has(r.product_id)) // drop virtuals from top level (folded under parent)
      .map(r => {
        const kids = byParent[r.product_id] || [];
        if (kids.length === 0) return r;
        const sumNum = (key) => Number(r[key] || 0) + kids.reduce((s, k) => s + Number(k[key] || 0), 0);
        const mergeByFac = (key) => {
          const out = { ...(r[key] || {}) };
          kids.forEach(k => Object.entries(k[key] || {}).forEach(([f, v]) => { out[f] = (out[f] || 0) + Number(v || 0); }));
          return out;
        };
        return {
          ...r,
          _virtual_children: kids,
          opening_total: sumNum('opening_total'),
          adjustment_in_range_total: sumNum('adjustment_in_range_total'),
          production_in_range_total: sumNum('production_in_range_total'),
          dispatch_in_range_total: sumNum('dispatch_in_range_total'),
          in_transit: sumNum('in_transit'),
          total: sumNum('total'),
          available: sumNum('available'),
          by_facility: mergeByFac('by_facility'),
          adjustment_in_range_by_facility: mergeByFac('adjustment_in_range_by_facility'),
          production_in_range_by_facility: mergeByFac('production_in_range_by_facility'),
          dispatch_in_range_by_facility: mergeByFac('dispatch_in_range_by_facility'),
          adjustment_entries: [
            ...(r.adjustment_entries || []),
            ...kids.flatMap(k => (k.adjustment_entries || [])),
          ].sort((a, b) => (a.date || '').localeCompare(b.date || '')),
          production_entries: [
            ...(r.production_entries || []),
            ...kids.flatMap(k => (k.production_entries || [])),
          ].sort((a, b) => (a.date || '').localeCompare(b.date || '')),
          dispatch_entries: [
            ...(r.dispatch_entries || []),
            ...kids.flatMap(k => (k.dispatch_entries || [])),
          ].sort((a, b) => (a.date || '').localeCompare(b.date || '')),
        };
      });
    return summed;
  })();
  // Sort: default = main products first (ascending by name), then virtual
  // products. When the operator clicks a sortable column header, override
  // with the requested key/direction — parents still stay grouped above
  // their virtual children so the tree structure isn't broken.
  filteredRows.sort((a, b) => {
    const va = a.is_virtual ? 1 : 0;
    const vb = b.is_virtual ? 1 : 0;
    if (va !== vb) return va - vb;
    if (stockSort.key && stockSort.dir) {
      if (stockSort.key === 'product') {
        const cmp = (a.product_name || '').localeCompare(b.product_name || '', undefined, { sensitivity: 'base' });
        return stockSort.dir === 'asc' ? cmp : -cmp;
      }
      if (stockSort.key === 'opening') {
        const av = Number(a.opening_total || 0);
        const bv = Number(b.opening_total || 0);
        if (av !== bv) return stockSort.dir === 'asc' ? av - bv : bv - av;
        return (a.product_name || '').localeCompare(b.product_name || '', undefined, { sensitivity: 'base' });
      }
      if (stockSort.key === 'available') {
        // "Stock Available" mirrors the same formula the table cell shows:
        //   date_to === today  →  closing_total − in_transit
        //   historical date_to →  closing_total  (in-transit isn't tracked historically)
        const isTodayView = dateTo === todayISO();
        const availFor = (r) => {
          const closing = (r.closing_total != null) ? Number(r.closing_total) : Number(r.total || 0);
          const it = Number(r.in_transit || 0);
          return isTodayView ? (closing - it) : closing;
        };
        const av = availFor(a);
        const bv = availFor(b);
        if (av !== bv) return stockSort.dir === 'asc' ? av - bv : bv - av;
        return (a.product_name || '').localeCompare(b.product_name || '', undefined, { sensitivity: 'base' });
      }
    }
    return (a.product_name || '').localeCompare(b.product_name || '', undefined, { sensitivity: 'base' });
  });
  const facilityTotals = (data?.facilities || []).reduce((acc, f) => {
    acc[f] = (data?.rows || []).reduce((s, r) => s + (r.by_facility[f] || 0), 0);
    return acc;
  }, {});
  const grandTotal = Object.values(facilityTotals).reduce((a, b) => a + b, 0);

  const { visible: visibleStockRows, hasMore: stockHasMore, remaining: stockRemaining, loadMoreRef: stockLoadMoreRef, showMore: stockShowMore } =
    useWindowedList(filteredRows, { resetDeps: [search, groupByParent, stockSort.key, stockSort.dir, dateFrom, dateTo, filteredRows.length] });

  return (
    <div data-testid="stock-section">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#2B5339' }}>Stock &amp; Delivery</h2>
        <div className="inline-flex rounded border overflow-hidden" style={{ borderColor: '#2B5339' }} data-testid="stock-subtabs">
          <button
            onClick={() => setStockSubTab('stock')}
            className="px-4 py-1.5 text-sm font-medium"
            style={{ background: stockSubTab === 'stock' ? '#2B5339' : 'white', color: stockSubTab === 'stock' ? 'white' : '#2B5339' }}
            data-testid="stock-subtab-stock"
          >Stock</button>
          <button
            onClick={() => setStockSubTab('delivery')}
            className="px-4 py-1.5 text-sm font-medium border-l"
            style={{ background: stockSubTab === 'delivery' ? '#2B5339' : 'white', color: stockSubTab === 'delivery' ? 'white' : '#2B5339', borderColor: '#2B5339' }}
            data-testid="stock-subtab-delivery"
          >Delivery</button>
        </div>
      </div>

      {stockSubTab === 'delivery' ? (
        <DeliveryView refreshKey={deliveryRefreshKey} products={products} orders={orders} transporters={transporters} userRole={userRole} onChanged={() => { load(); fetchData && fetchData(); }} />
      ) : (
      <>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        {/* Date selector — Today / Tomorrow / Edit range */}
        <div className="flex items-center flex-wrap gap-2" data-testid="stock-date-bar">
          <button
            type="button"
            onClick={() => applyPreset('today')}
            className="text-xs px-3 py-2 rounded font-medium border h-9"
            style={datePreset === 'today'
              ? { background: '#2B5339', color: 'white', borderColor: '#2B5339' }
              : { background: 'white', color: '#2B5339', borderColor: '#E2DFD8' }}
            data-testid="stock-date-today"
          >Today</button>
          <button
            type="button"
            onClick={() => applyPreset('tomorrow')}
            className="text-xs px-3 py-2 rounded font-medium border h-9"
            style={datePreset === 'tomorrow'
              ? { background: '#2B5339', color: 'white', borderColor: '#2B5339' }
              : { background: 'white', color: '#2B5339', borderColor: '#E2DFD8' }}
            data-testid="stock-date-tomorrow"
          >Tomorrow</button>
          <button
            type="button"
            onClick={() => setDatePreset('custom')}
            className="text-xs px-3 py-2 rounded font-medium border h-9 inline-flex items-center gap-1"
            style={datePreset === 'custom'
              ? { background: '#2B5339', color: 'white', borderColor: '#2B5339' }
              : { background: 'white', color: '#2B5339', borderColor: '#E2DFD8' }}
            data-testid="stock-date-edit"
          ><CalendarIcon className="w-3.5 h-3.5" />Edit</button>
          {datePreset === 'custom' && (
            <div className="inline-flex items-center gap-1.5 ml-1" data-testid="stock-date-custom-range">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9 w-40 text-xs"
                data-testid="stock-date-from"
              />
              <span className="text-xs" style={{ color: '#5C6B61' }}>to</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9 w-40 text-xs"
                data-testid="stock-date-to"
              />
            </div>
          )}
          <span className="text-[11px] ml-1" style={{ color: '#5C6B61' }}>
            {dateFrom === dateTo
              ? `Showing activity for ${dateFrom}`
              : `Showing activity ${dateFrom} → ${dateTo}`}
          </span>
          {/* Group-by-parent toggle (21/Feb/2026). When ON, virtual products
              fold under their semantic parent — parent row sums all numbers,
              expand chevron reveals children with their own per-row figures. */}
          <label className="inline-flex items-center gap-1.5 ml-3 text-xs cursor-pointer select-none" title="Stack virtual products under their parent main product. Each virtual still has its own stock and movements." data-testid="stock-group-toggle-label">
            <input
              type="checkbox"
              checked={groupByParent}
              onChange={(e) => setGroupByParent(e.target.checked)}
              className="h-3.5 w-3.5"
              style={{ accentColor: '#2B5339' }}
              data-testid="stock-group-toggle"
            />
            <span style={{ color: '#2B5339' }}>Group by parent</span>
          </label>
        </div>
        <div className="flex items-center flex-wrap gap-2 w-full sm:w-auto">
          <Input placeholder="Search product..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 w-full sm:w-72 bg-white" data-testid="stock-search" />
          {/* Expand / Collapse All — bulk-open every product row to inspect
              Production / Dispatch / Stock-Adj details at once, or collapse
              the lot to clean up the view. Uses filteredRows (current visible
              set) so it respects search + group-by-parent filters. */}
          {(() => {
            const visibleIds = (filteredRows || []).map((r) => r.product_id).filter(Boolean);
            if (visibleIds.length === 0) return null;
            const allOpen = visibleIds.every((id) => !!expanded[id]);
            return (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (allOpen) {
                    setExpanded({});
                  } else {
                    const next = { ...expanded };
                    visibleIds.forEach((id) => { next[id] = true; });
                    setExpanded(next);
                  }
                }}
                data-testid="stock-expand-toggle"
                title={allOpen ? 'Collapse every product row in view' : 'Expand every product row in view'}
              >
                {allOpen ? 'Collapse all' : 'Expand all'}
              </Button>
            );
          })()}
          <Button variant="outline" size="sm" onClick={() => downloadStock('excel')} disabled={downloading} data-testid="stock-export-excel-btn" title="Download Excel report for the selected date range">
            <Download className="w-4 h-4 mr-1" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadStock('pdf')} disabled={downloading} data-testid="stock-export-pdf-btn" title="Download PDF report for the selected date range">
            <FileText className="w-4 h-4 mr-1" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={openHistory} data-testid="stock-history-btn">
            <ClipboardList className="w-4 h-4 mr-1" /> History
          </Button>
          <Button variant="outline" size="sm" onClick={openAudit} data-testid="stock-audit-btn" title="Verify which dispatches are inflating the in_transit column">
            <Eye className="w-4 h-4 mr-1" /> Audit
          </Button>
        </div>
      </div>

      {/* Summary cards removed per user request (30/May/2026) — Products /
          Facility totals / Grand Total tiles were redundant with the table
          itself which already shows per-product and per-facility numbers. */}

      {/* Negative-OPENING-stock alerts strip. Admin-only — managers see a
          read-only stock view, so the corrective Fix tooling is hidden for
          them. A day's CLOSING stock can be temporarily negative (e.g.
          dispatches confirmed before production is synced) — that's a
          transient pipeline state, not a data error. But OPENING stock for a
          day represents the cumulative state before any activity in the
          range, so it must never be negative. Negative opening = historical
          data is broken (missed prod entry, double dispatch, etc.). The
          strip surfaces every (product × facility) with opening < 0 and
          gives a one-click "Fix" that posts the corrective delta. */}
      {isAdmin && (() => {
        const alerts = [];
        (data?.rows || []).forEach(r => {
          // Skip truly inactive virtual products (same hide-rule used in baseRows)
          if (r.is_virtual) {
            const hasActivity = (r.total || 0) !== 0
              || (r.in_transit || 0) !== 0
              || (r.production_in_range_total || 0) !== 0
              || (r.dispatch_in_range_total || 0) !== 0
              || (r.adjustment_in_range_total || 0) !== 0;
            if (!hasActivity) return;
          }
          (data?.facilities || []).forEach(f => {
            // Hide rows the operator just clicked Fix on — the manual
            // adjustment was applied; whether the formula re-balances or
            // not, they don't want to see the same suggestion again.
            if (dismissedAlerts.has(`${r.product_id}|${f}`)) return;
            // Use backend-supplied opening_by_facility when available
            // (period-accurate even for past ranges). Fall back to the
            // legacy formula for old API responses still in flight.
            const openingF = (r.opening_by_facility && (f in r.opening_by_facility))
              ? r.opening_by_facility[f]
              : (((r.by_facility || {})[f] || 0)
                 - ((r.production_in_range_by_facility || {})[f] || 0)
                 + ((r.dispatch_in_range_by_facility || {})[f] || 0)
                 - ((r.adjustment_in_range_by_facility || {})[f] || 0));
            if (openingF < 0) alerts.push({ row: r, facility: f, qty: openingF });
          });
        });
        if (alerts.length === 0) return null;
        alerts.sort((a, b) => a.qty - b.qty); // most negative first
        const totalShort = alerts.reduce((s, a) => s + a.qty, 0);
        return (
          <Card className="p-3 mb-4" style={{ background: '#FFF5F0', borderColor: '#E89D4F', borderWidth: 1 }} data-testid="stock-alerts-strip">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" style={{ color: '#C05C35' }} />
                <span className="text-sm font-semibold" style={{ color: '#C05C35' }} data-testid="stock-alerts-title">
                  Negative opening stock — {alerts.length} {alerts.length === 1 ? 'item' : 'items'} need correction
                </span>
              </div>
              <span className="text-xs font-semibold" style={{ color: '#C05C35' }} data-testid="stock-alerts-total">
                Total shortfall: {totalShort.toLocaleString('en-IN')} bags
              </span>
            </div>
            <p className="text-[11px] mb-2" style={{ color: '#5C6B61' }}>
              A day's closing stock can be temporarily negative while production sync is pending — that's normal. Opening stock is always cumulative-before-activity, so a negative value means historical data is missing or duplicated. Click <b>Fix</b> to add a corrective adjustment.
            </p>
            <div className="flex flex-wrap gap-2">
              {alerts.map(({ row, facility, qty }) => (
                <div
                  key={`${row.product_id}|${facility}`}
                  className="inline-flex items-center gap-2 px-2 py-1 rounded text-xs"
                  style={{ background: 'white', border: '1px solid #E89D4F' }}
                  data-testid={`stock-alert-${row.product_id}-${facility}`}
                >
                  <span className="font-medium" style={{ color: '#1F1F1F' }}>{row.product_name}</span>
                  <span style={{ color: '#5C6B61' }}>·</span>
                  <span style={{ color: '#5C6B61' }}>{facility}</span>
                  <span className="font-bold tabular-nums" style={{ color: '#C05C35' }}>{qty.toLocaleString('en-IN')}</span>
                  <button
                    type="button"
                    onClick={() => fixNegativeOpening(row, facility, qty)}
                    disabled={fixingKey === `${row.product_id}|${facility}`}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-semibold hover:bg-[#2B5339] hover:text-white border disabled:opacity-50"
                    style={{ borderColor: '#2B5339', color: '#2B5339' }}
                    data-testid={`stock-alert-fix-${row.product_id}-${facility}`}
                    title={`Apply +${Math.abs(qty)} bag adjustment to bring ${row.product_name} opening at ${facility} to 0`}
                  >
                    <Plus className="w-3 h-3" />{fixingKey === `${row.product_id}|${facility}` ? 'Fixing…' : 'Fix'}
                  </button>
                </div>
              ))}
            </div>
          </Card>
        );
      })()}

      <Card className="overflow-auto">
        <table className="w-full text-sm" data-testid="stock-table">
          <thead>
            <tr style={{ background: '#FBF9F6' }}>
              <th className="p-2 text-left w-6"></th>
              <th
                className="p-2 text-left cursor-pointer select-none hover:bg-[#FBF9F6]"
                onClick={() => cycleStockSort('product')}
                data-testid="stock-sort-product"
                title="Click to sort by Product (A → Z → default)"
              >
                Product{stockSort.key === 'product' ? (stockSort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
              </th>
              <th
                className="p-2 text-right cursor-pointer select-none hover:bg-[#FBF9F6]"
                onClick={() => cycleStockSort('opening')}
                data-testid="stock-sort-opening"
                title="Sum across facilities at the start of the date range. Click to sort ascending / descending / default."
              >
                Opening Stock{stockSort.key === 'opening' ? (stockSort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
              </th>
              <th className="p-2 text-right" title="Manual stock adjustments (kind=manual) inside the selected date range. Includes corrective entries, opening-stock additions, damaged/return deductions." style={{ color: '#5C6B61' }}>Stock Adj.</th>
              <th className="p-2 text-right" title="Bags produced in the selected date range" style={{ color: '#2B5339' }}>(+) Production</th>
              <th className="p-2 text-right" title="All bags dispatched in the range (delivered + in-transit). In-transit shown below in red." style={{ color: '#C05C35' }}>(-) Dispatch</th>
              <th className="p-2 text-right" title="Stock physically in the warehouse right now (in-transit bags have already left and are excluded)." style={{ color: '#2B5339' }}>Physical Stock</th>
              <th
                className="p-2 text-right cursor-pointer select-none hover:bg-[#FBF9F6]"
                title="Physical Stock − In-Transit (what's truly free for new dispatches; in-transit is already committed). Click to sort ascending / descending / default."
                style={{ color: '#2B5339' }}
                onClick={() => cycleStockSort('available')}
                data-testid="stock-sort-available"
              >
                Stock Available{stockSort.key === 'available' ? (stockSort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="p-8 text-center"><Loader2 className="inline w-5 h-5 animate-spin" /></td></tr>
            ) : filteredRows.length === 0 ? (
              <tr><td colSpan="8" className="p-8 text-center" style={{ color: '#5C6B61' }}>
                {products.length === 0 ? 'Add products first in the Products tab.' : 'No products match your search.'}
              </td></tr>
            ) : visibleStockRows.map(r => {
              const isOpen = !!expanded[r.product_id];
              const prodTotal = r.production_in_range_total || 0;
              const dispTotal = r.dispatch_in_range_total || 0;
              const adjTotal = r.adjustment_in_range_total || 0;
              const inTransit = r.in_transit || 0;
              // Per-user formulas (07/Jun/2026):
              //   Physical = closing physical stock as of the selected date range's
              //              end date (date_to). Backend `closing_total` /
              //              `closing_by_facility` are the correct historical
              //              snapshot; they equal live `total` / `by_facility`
              //              only when date_to is today.
              //   Available = Physical − In-Transit (only meaningful for TODAY,
              //              since in-transit is a right-now concept). For
              //              historical date_to we fall back to closing_total
              //              since historical in-transit isn't tracked.
              // Bug fix (11/Feb/2026): previously used `r.total` here, which is
              // the CURRENT (right-now) live stock — so picking a past date_to
              // in the Stock UI would leak today's Physical/Available values
              // into the historical view instead of showing the correct
              // closing snapshot for that day.
              const isToday = dateTo === todayISO();
              const closingTotal = (r.closing_total != null) ? r.closing_total : (r.total || 0);
              const closingByFacility = r.closing_by_facility || r.by_facility || {};
              const physical = closingTotal;
              const available = isToday ? (closingTotal - inTransit) : closingTotal;
              return (
              <React.Fragment key={r.product_id}>
              <tr className="border-t hover:bg-[#FBF9F6] cursor-pointer" style={{ borderColor: '#E2DFD8' }} data-testid="stock-row" onClick={() => toggleExpand(r.product_id)}>
                <td className="p-2 text-center" data-testid="stock-row-chevron">
                  {isOpen ? <ChevronDown className="w-4 h-4 inline" style={{ color: '#2B5339' }} /> : <ChevronRight className="w-4 h-4 inline" style={{ color: '#5C6B61' }} />}
                </td>
                <td className="p-2 font-medium">
                  {r.is_virtual && (
                    <span className="inline-block text-[9px] font-bold px-1 py-0.5 mr-1.5 rounded align-middle" style={{ background: '#FBF9F6', color: '#C05C35', border: '1px solid #C05C35' }} title="Virtual product — variant auto-spawned from a MISC order or sub-brand production row">VIRTUAL</span>
                  )}
                  {r.product_name}
                  {r.unit && <span className="text-xs ml-1" style={{ color: '#5C6B61' }}>({r.unit})</span>}
                </td>
                <td className="p-2 text-right font-medium" data-testid="stock-opening">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-block px-1 -mx-1 rounded hover:bg-[#FBF9F6] underline decoration-dotted decoration-[#5C6B61] underline-offset-2 cursor-pointer"
                        data-testid="stock-opening-btn"
                        title="Click for facility-wise opening stock"
                      >
                        {Number(r.opening_total || 0).toLocaleString('en-IN')}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 p-3" onClick={(e) => e.stopPropagation()} data-testid="stock-opening-popover">
                      <div className="text-xs font-semibold mb-2" style={{ color: '#2B5339' }}>Opening Stock — {r.product_name}</div>
                      <div className="space-y-1">
                        {(data?.facilities || []).map((f) => {
                          // Prefer backend-supplied opening_by_facility (period-
                          // accurate). Fall back to live-stock formula only for
                          // legacy responses that pre-date the 16/Feb/2026 fix.
                          const openingF = (r.opening_by_facility && (f in r.opening_by_facility))
                            ? r.opening_by_facility[f]
                            : (((r.by_facility && r.by_facility[f]) || 0)
                               - ((r.production_in_range_by_facility && r.production_in_range_by_facility[f]) || 0)
                               + ((r.dispatch_in_range_by_facility && r.dispatch_in_range_by_facility[f]) || 0)
                               - ((r.adjustment_in_range_by_facility && r.adjustment_in_range_by_facility[f]) || 0));
                          return (
                            <div key={f} className="flex items-center justify-between text-xs" data-testid={`stock-opening-pop-${f}`}>
                              <span style={{ color: '#5C6B61' }}>{f}</span>
                              <span className="font-bold tabular-nums" style={{ color: openingF < 0 ? '#C05C35' : '#1F1F1F' }}>{openingF.toLocaleString('en-IN')}</span>
                            </div>
                          );
                        })}
                        <div className="flex items-center justify-between text-xs pt-1.5 mt-1 border-t" style={{ borderColor: '#E2DFD8' }}>
                          <span className="font-semibold" style={{ color: '#2B5339' }}>Total</span>
                          <span className="font-bold tabular-nums" style={{ color: '#2B5339' }}>{Number(r.opening_total || 0).toLocaleString('en-IN')}</span>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </td>
                <td className="p-2 text-right font-medium" data-testid="stock-adj-range" title="Manual stock adjustments inside this date range (kind=manual)">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-block px-1 -mx-1 rounded hover:bg-[#FBF9F6] underline decoration-dotted decoration-[#5C6B61] underline-offset-2 cursor-pointer"
                        style={{ color: adjTotal === 0 ? '#5C6B61' : (adjTotal > 0 ? '#2B5339' : '#C05C35') }}
                        data-testid="stock-adj-btn"
                      >
                        {adjTotal === 0 ? '0' : `${adjTotal > 0 ? '+' : ''}${adjTotal.toLocaleString('en-IN')}`}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 p-3" onClick={(e) => e.stopPropagation()} data-testid="stock-adj-popover">
                      <div className="text-xs font-semibold mb-2" style={{ color: '#2B5339' }}>Stock Adj. — {r.product_name}</div>
                      <div className="space-y-1">
                        {(data?.facilities || []).map((f) => {
                          const adjF = (r.adjustment_in_range_by_facility && r.adjustment_in_range_by_facility[f]) || 0;
                          return (
                            <div key={f} className="flex items-center justify-between text-xs" data-testid={`stock-adj-pop-${f}`}>
                              <span style={{ color: '#5C6B61' }}>{f}</span>
                              <span className="font-bold tabular-nums" style={{ color: adjF === 0 ? '#5C6B61' : (adjF > 0 ? '#2B5339' : '#C05C35') }}>
                                {adjF === 0 ? '0' : `${adjF > 0 ? '+' : ''}${adjF.toLocaleString('en-IN')}`}
                              </span>
                            </div>
                          );
                        })}
                        <div className="flex items-center justify-between text-xs pt-1.5 mt-1 border-t" style={{ borderColor: '#E2DFD8' }}>
                          <span className="font-semibold" style={{ color: '#2B5339' }}>Total</span>
                          <span className="font-bold tabular-nums" style={{ color: adjTotal === 0 ? '#5C6B61' : (adjTotal > 0 ? '#2B5339' : '#C05C35') }}>
                            {adjTotal === 0 ? '0' : `${adjTotal > 0 ? '+' : ''}${adjTotal.toLocaleString('en-IN')}`}
                          </span>
                        </div>
                        <div className="text-[10px] mt-2 pt-1.5 border-t" style={{ color: '#5C6B61', borderColor: '#E2DFD8' }}>
                          Expand the row for the full ledger (date · reason · by · after).
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </td>
                <td className="p-2 text-right font-medium" data-testid="stock-prod-range">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-block px-1 -mx-1 rounded hover:bg-[#FBF9F6] underline decoration-dotted decoration-[#5C6B61] underline-offset-2 cursor-pointer"
                        style={{ color: prodTotal > 0 ? '#2B5339' : '#5C6B61' }}
                        data-testid="stock-prod-btn"
                      >
                        {prodTotal > 0 ? `+${prodTotal.toLocaleString('en-IN')}` : '0'}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[420px] p-3" onClick={(e) => e.stopPropagation()} data-testid="stock-prod-popover">
                      <div className="text-xs font-semibold mb-2" style={{ color: '#2B5339' }}>Production — {r.product_name}</div>
                      {(r.production_entries || []).length === 0 ? (
                        <div className="text-xs" style={{ color: '#5C6B61' }}>No production entries in this range.</div>
                      ) : (
                        <div className="max-h-72 overflow-auto rounded" style={{ border: '1px solid #E2DFD8' }}>
                          <table className="w-full text-[11px]">
                            <thead style={{ background: '#FBF9F6', color: '#5C6B61' }}>
                              <tr>
                                <th className="text-left px-2 py-1">Date</th>
                                <th className="text-left px-2 py-1">Fac</th>
                                <th className="text-left px-2 py-1">Lot</th>
                                <th className="text-left px-2 py-1">Variant</th>
                                <th className="text-right px-2 py-1">Qty</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(r.production_entries || []).map((e, idx) => (
                                <tr key={idx} className="border-t" style={{ borderColor: '#E2DFD8' }} data-testid="stock-prod-entry-row">
                                  <td className="px-2 py-1 whitespace-nowrap">{e.date || '-'}</td>
                                  <td className="px-2 py-1">{e.facility || '-'}</td>
                                  <td className="px-2 py-1">
                                    {e.sheet_id && e.lot_no != null && onNavigateToProduction ? (
                                      <button
                                        type="button"
                                        onClick={(ev) => { ev.stopPropagation(); onNavigateToProduction(e.sheet_id); }}
                                        className="underline decoration-dotted underline-offset-2 hover:text-[#2B5339] cursor-pointer"
                                        style={{ color: '#2B5339' }}
                                        data-testid={`stock-prod-entry-lot-${e.sheet_id}`}
                                        title={`Open Lot #${e.lot_no} in Production`}
                                      >
                                        #{e.lot_no}
                                      </button>
                                    ) : (
                                      e.lot_no != null ? `#${e.lot_no}` : '-'
                                    )}
                                  </td>
                                  <td className="px-2 py-1" style={{ color: '#5C6B61' }}>
                                    {[e.sub_brand_name, e.weight_kg ? `${e.weight_kg}kg` : '', e.variant].filter(Boolean).join(' · ') || '-'}
                                  </td>
                                  <td className="px-2 py-1 text-right font-semibold tabular-nums" style={{ color: '#2B5339' }}>+{Number(e.quantity || 0).toLocaleString('en-IN')}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr style={{ background: '#FBF9F6' }}>
                                <td className="px-2 py-1 font-semibold" colSpan={4} style={{ color: '#2B5339' }}>Total</td>
                                <td className="px-2 py-1 text-right font-bold tabular-nums" style={{ color: '#2B5339' }}>+{prodTotal.toLocaleString('en-IN')}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                </td>
                <td className="p-2 text-right font-medium align-top" data-testid="stock-dispatch-cell">
                  {/* Dispatch qty shown without minus sign — red color is the cue. */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-block px-1 -mx-1 rounded hover:bg-[#FBF9F6] underline decoration-dotted decoration-[#5C6B61] underline-offset-2 cursor-pointer"
                        style={{ color: dispTotal > 0 ? '#C05C35' : '#5C6B61' }}
                        data-testid="stock-disp-btn"
                      >
                        {dispTotal > 0 ? dispTotal.toLocaleString('en-IN') : '0'}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[480px] p-3" onClick={(e) => e.stopPropagation()} data-testid="stock-disp-popover">
                      <div className="text-xs font-semibold mb-2" style={{ color: '#C05C35' }}>Dispatch — {r.product_name}</div>
                      {(r.dispatch_entries || []).length === 0 ? (
                        <div className="text-xs" style={{ color: '#5C6B61' }}>No dispatch entries in this range.</div>
                      ) : (
                        <div className="max-h-72 overflow-auto rounded" style={{ border: '1px solid #E2DFD8' }}>
                          <table className="w-full text-[11px]">
                            <thead style={{ background: '#FBF9F6', color: '#5C6B61' }}>
                              <tr>
                                <th className="text-left px-2 py-1">Date</th>
                                <th className="text-left px-2 py-1">Order</th>
                                <th className="text-left px-2 py-1">Dealer</th>
                                <th className="text-left px-2 py-1">Fac</th>
                                <th className="text-left px-2 py-1">Vehicle</th>
                                <th className="text-right px-2 py-1">Qty</th>
                                <th className="text-left px-2 py-1">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(r.dispatch_entries || []).map((e, idx) => (
                                <tr key={idx} className="border-t" style={{ borderColor: '#E2DFD8' }} data-testid="stock-disp-entry-row">
                                  <td className="px-2 py-1 whitespace-nowrap">{e.date || '-'}</td>
                                  <td className="px-2 py-1 font-mono">
                                    {e.order_id ? (
                                      <button
                                        type="button"
                                        onClick={(ev) => { ev.stopPropagation(); setReviewOrderId(e.order_id); }}
                                        className="underline decoration-dotted underline-offset-2 hover:text-[#2B5339] cursor-pointer"
                                        style={{ color: '#2B5339' }}
                                        data-testid={`stock-disp-entry-order-${e.order_id}`}
                                        title="Click to view full order details"
                                      >
                                        {e.order_short || (e.order_id || '').slice(0, 8)}
                                      </button>
                                    ) : (
                                      e.order_short || (e.order_id || '').slice(0, 8) || '-'
                                    )}
                                  </td>
                                  <td className="px-2 py-1">{e.dealer_name || '-'}</td>
                                  <td className="px-2 py-1">{e.facility || '-'}</td>
                                  <td className="px-2 py-1" style={{ color: '#5C6B61' }}>{e.vehicle || '-'}</td>
                                  <td className="px-2 py-1 text-right font-semibold tabular-nums" style={{ color: '#C05C35' }}>{Number(e.quantity || 0).toLocaleString('en-IN')}</td>
                                  <td className="px-2 py-1">
                                    {e.delivered ? (
                                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: '#2B5339', color: 'white' }}>DELIVERED</span>
                                    ) : (
                                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: '#C05C35', color: 'white' }}>IN TRANSIT</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr style={{ background: '#FBF9F6' }}>
                                <td className="px-2 py-1 font-semibold" colSpan={5} style={{ color: '#C05C35' }}>Total Dispatched</td>
                                <td className="px-2 py-1 text-right font-bold tabular-nums" style={{ color: '#C05C35' }}>{dispTotal.toLocaleString('en-IN')}</td>
                                <td className="px-2 py-1 text-[10px]" style={{ color: '#C05C35' }}>{inTransit > 0 ? `In Transit: ${inTransit.toLocaleString('en-IN')}` : ''}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                  {inTransit > 0 && (
                    <div className="text-[10px] font-semibold mt-0.5" style={{ color: '#C05C35' }} data-testid="stock-in-transit" title="Bags currently in transit (dispatched but not yet delivered)">
                      In Transit: {inTransit.toLocaleString('en-IN')}
                    </div>
                  )}
                </td>
                <td className="p-2 text-right font-bold" data-testid="stock-physical">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-block px-1 -mx-1 rounded hover:bg-[#FBF9F6] underline decoration-dotted decoration-[#5C6B61] underline-offset-2 cursor-pointer"
                        style={{ color: '#2B5339' }}
                        data-testid="stock-physical-btn"
                      >
                        {physical.toLocaleString('en-IN')}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 p-3" onClick={(e) => e.stopPropagation()} data-testid="stock-physical-popover">
                      <div className="text-xs font-semibold mb-2" style={{ color: '#2B5339' }}>Physical Stock — {r.product_name}</div>
                      <div className="space-y-1">
                        {(data?.facilities || []).map((f) => {
                          const closing = (closingByFacility && closingByFacility[f]) || 0;
                          return (
                            <div key={f} className="flex items-center justify-between text-xs" data-testid={`stock-physical-pop-${f}`}>
                              <span style={{ color: '#5C6B61' }}>{f}</span>
                              <span className="font-bold tabular-nums" style={{ color: closing < 0 ? '#C05C35' : '#1F1F1F' }}>{closing.toLocaleString('en-IN')}</span>
                            </div>
                          );
                        })}
                        <div className="flex items-center justify-between text-xs pt-1.5 mt-1 border-t" style={{ borderColor: '#E2DFD8' }}>
                          <span className="font-semibold" style={{ color: '#2B5339' }}>Total</span>
                          <span className="font-bold tabular-nums" style={{ color: '#2B5339' }}>{physical.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="text-[10px] mt-2 pt-1.5 border-t" style={{ color: '#5C6B61', borderColor: '#E2DFD8' }}>
                          {isToday
                            ? <>Physical = stock actually sitting in the warehouse right now. In-transit bags ({inTransit.toLocaleString('en-IN')}) have already left and are not counted here.</>
                            : <>Physical = closing stock as of <b>{dateTo}</b> (historical snapshot). In-transit is a right-now concept and is not applied to historical dates.</>
                          }
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </td>
                <td className="p-2 text-right font-bold" data-testid="stock-available">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-block px-1 -mx-1 rounded hover:bg-[#FBF9F6] underline decoration-dotted decoration-[#5C6B61] underline-offset-2 cursor-pointer"
                        style={{ color: '#2B5339' }}
                        data-testid="stock-available-btn"
                      >
                        {available.toLocaleString('en-IN')}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 p-3" onClick={(e) => e.stopPropagation()} data-testid="stock-available-popover">
                      <div className="text-xs font-semibold mb-2" style={{ color: '#2B5339' }}>Stock Available — {r.product_name}</div>
                      <div className="space-y-1">
                        {(data?.facilities || []).map((f) => {
                          const closing = (closingByFacility && closingByFacility[f]) || 0;
                          return (
                            <div key={f} className="flex items-center justify-between text-xs" data-testid={`stock-available-pop-${f}`}>
                              <span style={{ color: '#5C6B61' }}>{f}</span>
                              <span className="font-bold tabular-nums" style={{ color: closing < 0 ? '#C05C35' : '#1F1F1F' }}>{closing.toLocaleString('en-IN')}</span>
                            </div>
                          );
                        })}
                        {isToday && inTransit > 0 && (
                          <div className="flex items-center justify-between text-xs" data-testid="stock-available-pop-in-transit" title="In transit (dispatched but not yet delivered) — already committed, subtracted from Available">
                            <span style={{ color: '#5C6B61' }}>(−) In Transit</span>
                            <span className="font-bold tabular-nums" style={{ color: '#C05C35' }}>{inTransit.toLocaleString('en-IN')}</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between text-xs pt-1.5 mt-1 border-t" style={{ borderColor: '#E2DFD8' }}>
                          <span className="font-semibold" style={{ color: '#2B5339' }}>Total</span>
                          <span className="font-bold tabular-nums" style={{ color: available < 0 ? '#C05C35' : '#2B5339' }}>{available.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="text-[10px] mt-2 pt-1.5 border-t" style={{ color: '#5C6B61', borderColor: '#E2DFD8' }}>
                          {isToday
                            ? <>Available = Physical − In-Transit. This is what's free for new dispatches.</>
                            : <>Available = closing stock as of <b>{dateTo}</b>. In-transit is a right-now concept and is not applied to historical dates.</>
                          }
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </td>
              </tr>
              {isOpen && (
                <tr style={{ background: '#FBF9F6' }} data-testid="stock-row-expanded">
                  <td></td>
                  <td colSpan="7" className="p-3">
                    {/* Adjust-stock action row. Facility-wise breakdowns + ledger
                        are now available as popovers on the Opening/Stock Adj/
                        Production/Dispatch columns, so we keep the expand panel
                        focused on actions + virtual children rollup. */}
                    <div className="flex flex-wrap items-center justify-end gap-1 mb-3" onClick={(e) => e.stopPropagation()}>
                      {isAdmin && (data?.facilities || []).map(f => (
                        <button
                          key={f}
                          onClick={() => openAdjust(r, f)}
                          title={`Adjust ${r.product_name} at ${f}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border hover:bg-[#FBF9F6]"
                          style={{ borderColor: '#2B5339', color: '#2B5339' }}
                          data-testid={`stock-adjust-${f}-btn`}
                        >
                          <Plus className="w-3 h-3" />Adjust {f}
                        </button>
                      ))}
                    </div>
                    {/* Date-wise breakdown — only when the operator picked a
                        range spanning more than 1 day. Rolls the row's
                        opening_total (start-of-range snapshot) forward day-
                        by-day using the entries the backend already ships
                        (adjustments/production/dispatch) so every column matches
                        the parent row's arithmetic exactly:
                          closing(D)  = opening(D) + adj(D) + prod(D) − disp(D)
                          opening(D+1)= closing(D)
                        In-transit is a right-now concept and only applies to
                        today's row; for historical days it stays 0. */}
                    {dateFrom && dateTo && dateFrom !== dateTo && (() => {
                      // Build inclusive date list.
                      const days = [];
                      const start = new Date(dateFrom + 'T00:00:00');
                      const end = new Date(dateTo + 'T00:00:00');
                      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                        const iso = d.toISOString().slice(0, 10);
                        days.push(iso);
                      }
                      const cap = 60;
                      if (days.length > cap) return (
                        <div className="text-[11px] mt-3 pt-3 border-t" style={{ color: '#5C6B61', borderColor: '#E2DFD8' }}>
                          Date-wise breakdown is only shown for ranges up to {cap} days ({days.length} selected). Please narrow the range to see per-day details.
                        </div>
                      );
                      // Group each entry-type by date. Quantity conventions match the parent row.
                      const prodByDate = {};
                      (r.production_entries || []).forEach(e => { prodByDate[e.date] = (prodByDate[e.date] || 0) + Number(e.quantity || 0); });
                      const dispByDate = {};
                      const inTransitByDate = {};
                      (r.dispatch_entries || []).forEach(e => {
                        dispByDate[e.date] = (dispByDate[e.date] || 0) + Number(e.quantity || 0);
                        if (!e.delivered) inTransitByDate[e.date] = (inTransitByDate[e.date] || 0) + Number(e.quantity || 0);
                      });
                      const adjByDate = {};
                      (r.adjustment_entries || []).forEach(e => { adjByDate[e.date] = (adjByDate[e.date] || 0) + Number(e.delta || 0); });
                      // Roll the opening forward.
                      const rows = [];
                      let openingD = Number(r.opening_total || 0);
                      const todayStr = todayISO();
                      days.forEach((d) => {
                        const adj = adjByDate[d] || 0;
                        const prod = prodByDate[d] || 0;
                        const disp = dispByDate[d] || 0;
                        const it = inTransitByDate[d] || 0;
                        const closing = openingD + adj + prod - disp;
                        // Physical for that day = closing snapshot.
                        // Available: Physical − in-transit-of-that-day (only meaningful for today).
                        const isTodayD = d === todayStr;
                        const available = isTodayD ? (closing - it) : closing;
                        rows.push({
                          date: d,
                          opening: openingD,
                          adj, prod, disp,
                          in_transit: it,
                          physical: closing,
                          available,
                        });
                        openingD = closing;
                      });
                      const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
                      const signed = (n) => (n > 0 ? `+${fmt(n)}` : (n < 0 ? fmt(n) : '0'));
                      return (
                        <div className="mt-3 pt-3 border-t" style={{ borderColor: '#E2DFD8' }} data-testid="stock-daywise-panel">
                          <div className="text-xs font-semibold mb-2" style={{ color: '#2B5339' }}>
                            📅 Date-wise breakdown — {r.product_name} ({days.length} days)
                          </div>
                          <div className="overflow-auto rounded" style={{ border: '1px solid #E2DFD8' }}>
                            <table className="w-full text-[11px]">
                              <thead style={{ background: 'white' }}>
                                <tr style={{ color: '#5C6B61' }}>
                                  <th className="text-left px-2 py-1">Date</th>
                                  <th className="text-right px-2 py-1">Opening</th>
                                  <th className="text-right px-2 py-1">Stock Adj.</th>
                                  <th className="text-right px-2 py-1" style={{ color: '#2B5339' }}>(+) Production</th>
                                  <th className="text-right px-2 py-1" style={{ color: '#C05C35' }}>(−) Dispatch</th>
                                  <th className="text-right px-2 py-1" style={{ color: '#C05C35' }}>In Transit</th>
                                  <th className="text-right px-2 py-1" style={{ color: '#2B5339' }}>Physical</th>
                                  <th className="text-right px-2 py-1" style={{ color: '#2B5339' }}>Available</th>
                                </tr>
                              </thead>
                              <tbody>
                                {rows.map((dr) => (
                                  <tr key={dr.date} className="border-t" style={{ borderColor: '#E2DFD8' }} data-testid={`stock-daywise-row-${dr.date}`}>
                                    <td className="px-2 py-1 whitespace-nowrap font-medium">{dr.date}</td>
                                    <td className="px-2 py-1 text-right tabular-nums" style={{ color: dr.opening < 0 ? '#C05C35' : '#1F1F1F' }}>{fmt(dr.opening)}</td>
                                    <td className="px-2 py-1 text-right tabular-nums" style={{ color: dr.adj === 0 ? '#5C6B61' : (dr.adj > 0 ? '#2B5339' : '#C05C35') }}>{signed(dr.adj)}</td>
                                    <td className="px-2 py-1 text-right tabular-nums" style={{ color: dr.prod > 0 ? '#2B5339' : '#5C6B61' }}>{dr.prod > 0 ? `+${fmt(dr.prod)}` : '0'}</td>
                                    <td className="px-2 py-1 text-right tabular-nums" style={{ color: dr.disp > 0 ? '#C05C35' : '#5C6B61' }}>{dr.disp > 0 ? fmt(dr.disp) : '0'}</td>
                                    <td className="px-2 py-1 text-right tabular-nums" style={{ color: dr.in_transit > 0 ? '#C05C35' : '#5C6B61' }}>{dr.in_transit > 0 ? fmt(dr.in_transit) : '0'}</td>
                                    <td className="px-2 py-1 text-right tabular-nums font-semibold" style={{ color: dr.physical < 0 ? '#C05C35' : '#2B5339' }}>{fmt(dr.physical)}</td>
                                    <td className="px-2 py-1 text-right tabular-nums font-semibold" style={{ color: dr.available < 0 ? '#C05C35' : '#2B5339' }}>{fmt(dr.available)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="text-[10px] mt-2" style={{ color: '#5C6B61' }}>
                            Rolled from the row&apos;s opening at {dateFrom}. Each row&apos;s Physical becomes the next day&apos;s Opening.
                          </div>
                        </div>
                      );
                    })()}
                    {/* Virtual children panel — only visible in Group-by-parent
                        mode. Each child is a virtual product with its OWN
                        stock movements; this just lists them so the operator
                        can see what's nested under this parent. Each child
                        name is clickable to (eventually) drill into that
                        virtual product's standalone view. */}
                    {(r._virtual_children && r._virtual_children.length > 0) && (
                      <div className="mt-3 pt-3 border-t" style={{ borderColor: '#E2DFD8' }} data-testid="stock-virtual-children-panel">
                        <div className="text-xs font-semibold mb-2" style={{ color: '#2B5339' }}>
                          ↳ Virtual variants ({r._virtual_children.length})
                        </div>
                        <table className="w-full text-xs">
                          <thead>
                            <tr style={{ color: '#5C6B61' }}>
                              <th className="text-left p-1">Variant</th>
                              <th className="text-right p-1">Opening</th>
                              <th className="text-right p-1">Adj.</th>
                              <th className="text-right p-1" style={{ color: '#2B5339' }}>(+) Prod</th>
                              <th className="text-right p-1" style={{ color: '#C05C35' }}>(-) Disp</th>
                              <th className="text-right p-1">In Transit</th>
                              <th className="text-right p-1" style={{ color: '#2B5339' }}>Physical</th>
                              <th className="text-right p-1" style={{ color: '#2B5339' }}>Available</th>
                            </tr>
                          </thead>
                          <tbody>
                            {r._virtual_children.map((c) => {
                              const cIT = c.in_transit || 0;
                              const cAdj = c.adjustment_in_range_total || 0;
                              // Same formula as parent: Physical = on-books stock,
                              // Available = Physical − In-Transit.
                              const cPhysical = c.total || 0;
                              const cAvail = (c.total || 0) - cIT;
                              return (
                                <tr key={c.product_id} className="border-t" style={{ borderColor: '#E2DFD8' }} data-testid="stock-virtual-child-row">
                                  <td className="p-1 font-medium" style={{ color: '#2B5339' }}>↳ {c.product_name}</td>
                                  <td className="p-1 text-right">{Number(c.opening_total || 0).toLocaleString('en-IN')}</td>
                                  <td className="p-1 text-right" style={{ color: cAdj === 0 ? '#5C6B61' : (cAdj > 0 ? '#2B5339' : '#C05C35') }}>{cAdj === 0 ? '0' : `${cAdj > 0 ? '+' : ''}${cAdj.toLocaleString('en-IN')}`}</td>
                                  <td className="p-1 text-right" style={{ color: '#2B5339' }}>{(c.production_in_range_total || 0) > 0 ? `+${(c.production_in_range_total || 0).toLocaleString('en-IN')}` : '0'}</td>
                                  <td className="p-1 text-right" style={{ color: '#C05C35' }}>{(c.dispatch_in_range_total || 0) > 0 ? (c.dispatch_in_range_total || 0).toLocaleString('en-IN') : '0'}</td>
                                  <td className="p-1 text-right" style={{ color: cIT > 0 ? '#C05C35' : '#5C6B61' }}>{cIT > 0 ? cIT.toLocaleString('en-IN') : '0'}</td>
                                  <td className="p-1 text-right font-semibold" style={{ color: '#2B5339' }}>{cPhysical.toLocaleString('en-IN')}</td>
                                  <td className="p-1 text-right font-semibold" style={{ color: '#2B5339' }}>{cAvail.toLocaleString('en-IN')}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </td>
                </tr>
              )}
              </React.Fragment>
              );
            })}
            {stockHasMore && (
              <tr ref={stockLoadMoreRef} data-testid="stock-load-more">
                <td colSpan="8" className="p-3 text-center" style={{ background: '#FBF9F6' }}>
                  <Button variant="outline" size="sm" onClick={stockShowMore} data-testid="stock-show-more-btn">
                    Show more ({stockRemaining} more)
                  </Button>
                </td>
              </tr>
            )}
          </tbody>
          {filteredRows.length > 0 && (
            <tfoot>
              <tr style={{ background: '#FBF9F6' }}>
                <td></td>
                <td className="p-2 font-bold">TOTAL</td>
                <td className="p-2 text-right font-bold" style={{ color: '#2B5339' }}>
                  {filteredRows.reduce((s, r) => s + Number(r.opening_total || 0), 0).toLocaleString('en-IN')}
                </td>
                <td className="p-2 text-right font-bold">
                  {(() => {
                    const t = filteredRows.reduce((s, r) => s + (r.adjustment_in_range_total || 0), 0);
                    if (t === 0) return <span style={{ color: '#5C6B61' }}>0</span>;
                    return <span style={{ color: t > 0 ? '#2B5339' : '#C05C35' }}>{t > 0 ? '+' : ''}{t.toLocaleString('en-IN')}</span>;
                  })()}
                </td>
                <td className="p-2 text-right font-bold" style={{ color: '#2B5339' }}>
                  {(() => { const t = filteredRows.reduce((s, r) => s + (r.production_in_range_total || 0), 0); return t > 0 ? `+${t.toLocaleString('en-IN')}` : '0'; })()}
                </td>
                <td className="p-2 text-right font-bold align-top">
                  <div style={{ color: '#C05C35' }}>
                    {(() => { const t = filteredRows.reduce((s, r) => s + (r.dispatch_in_range_total || 0), 0); return t > 0 ? t.toLocaleString('en-IN') : '0'; })()}
                  </div>
                  {(() => {
                    const t = filteredRows.reduce((s, r) => s + (r.in_transit || 0), 0);
                    if (t <= 0) return null;
                    return (
                      <div className="text-[10px] font-semibold mt-0.5" style={{ color: '#C05C35' }}>
                        In Transit: {t.toLocaleString('en-IN')}
                      </div>
                    );
                  })()}
                </td>
                <td className="p-2 text-right font-bold" style={{ color: '#2B5339' }} data-testid="stock-tfoot-physical">
                  {grandTotal.toLocaleString('en-IN')}
                </td>
                <td className="p-2 text-right font-bold" style={{ color: '#2B5339' }} data-testid="stock-tfoot-available">
                  {(grandTotal - filteredRows.reduce((s, r) => s + (r.in_transit || 0), 0)).toLocaleString('en-IN')}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </Card>

      {/* Adjust dialog */}
      <Dialog open={!!adjust} onOpenChange={(o) => { if (!o) setAdjust(null); }}>
        <DialogContent className="max-w-md" data-testid="stock-adjust-dialog">
          <DialogHeader>
            <DialogTitle>Adjust Stock</DialogTitle>
          </DialogHeader>
          {adjust && (
            <div className="space-y-3">
              <div className="text-sm">
                <div><span style={{ color: '#5C6B61' }}>Product:</span> <span className="font-semibold">{adjust.product_name}</span></div>
                <div><span style={{ color: '#5C6B61' }}>Facility:</span> <span className="font-semibold">{adjust.facility}</span></div>
                <div><span style={{ color: '#5C6B61' }}>Current:</span> <span className="font-semibold">{adjust.current.toLocaleString('en-IN')} bags</span></div>
              </div>
              <div>
                <div className="text-xs mb-1" style={{ color: '#5C6B61' }}>Change (bags)</div>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setDelta(String((parseInt(delta || '0', 10) || 0) - 10))} data-testid="stock-delta-minus-btn"><Minus className="w-3 h-3" /></Button>
                  <Input
                    type="number"
                    value={delta}
                    onChange={(e) => setDelta(e.target.value)}
                    placeholder="e.g. 50 or -20"
                    className="flex-1"
                    data-testid="stock-delta-input"
                  />
                  <Button type="button" variant="outline" size="sm" onClick={() => setDelta(String((parseInt(delta || '0', 10) || 0) + 10))} data-testid="stock-delta-plus-btn"><Plus className="w-3 h-3" /></Button>
                </div>
                <div className="text-[11px] mt-1" style={{ color: '#5C6B61' }}>
                  Positive = add to stock · Negative = deduct (e.g., damaged/returned)
                </div>
                {adjust.current < 0 && (
                  <button
                    type="button"
                    onClick={() => { setDelta(String(-adjust.current)); if (!reason) setReason('Corrective production entry'); }}
                    className="text-[11px] mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded border font-semibold hover:bg-[#2B5339] hover:text-white"
                    style={{ borderColor: '#2B5339', color: '#2B5339' }}
                    data-testid="stock-bring-to-zero-btn"
                    title="Pre-fill the change as +|current| so the resulting qty is exactly 0"
                  >
                    Bring to 0 (+{Math.abs(adjust.current).toLocaleString('en-IN')})
                  </button>
                )}
                {delta && !Number.isNaN(parseInt(delta, 10)) && (
                  <div className="text-xs mt-1">
                    New qty will be: <span className="font-bold" style={{ color: (adjust.current + (parseInt(delta, 10) || 0)) < 0 ? '#C05C35' : '#2B5339' }}>
                      {(adjust.current + (parseInt(delta, 10) || 0)).toLocaleString('en-IN')}
                    </span>
                  </div>
                )}
              </div>
              <div>
                <div className="text-xs mb-1" style={{ color: '#5C6B61' }}>Reason (optional)</div>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Opening stock / Damaged / Return / Correction..."
                  data-testid="stock-reason-input"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAdjust(null)} disabled={saving}>Cancel</Button>
                <Button onClick={submitAdjust} disabled={saving} style={{ background: '#2B5339' }} data-testid="stock-adjust-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* In-Transit Audit dialog */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="stock-audit-dialog">
          <DialogHeader>
            <DialogTitle>In-Transit Audit</DialogTitle>
          </DialogHeader>
          {auditLoading ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-6" /> : !auditData ? (
            <p className="text-sm" style={{ color: '#C05C35' }}>Failed to load.</p>
          ) : (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Card className="p-3">
                  <div className="text-xs" style={{ color: '#5C6B61' }}>Delivery view — active dispatches</div>
                  <div className="text-2xl font-bold" style={{ color: '#2B5339' }} data-testid="audit-delivery-active-count">{auditData.delivery_view_active_count}</div>
                </Card>
                <Card className="p-3">
                  <div className="text-xs" style={{ color: '#5C6B61' }}>Stock tab — in_transit dispatches (facility-less)</div>
                  <div className="text-2xl font-bold" style={{ color: '#C05C35' }} data-testid="audit-stock-in-transit-count">{auditData.stock_in_transit_dispatch_count}</div>
                </Card>
              </div>
              <p className="text-xs italic" style={{ color: '#5C6B61' }}>{auditData.discrepancy_explanation}</p>

              {auditData.stock_in_transit_products && auditData.stock_in_transit_products.length > 0 && (
                <section>
                  <h4 className="font-semibold mb-1" style={{ color: '#2B5339' }}>Products contributing to in_transit ({auditData.stock_in_transit_products.length})</h4>
                  <table className="w-full text-xs border">
                    <thead style={{ background: '#FBF9F6' }}>
                      <tr>
                        <th className="p-2 text-left">Product</th>
                        <th className="p-2 text-right">Qty</th>
                        <th className="p-2 text-left">Dispatch IDs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditData.stock_in_transit_products.map((p, i) => (
                        <tr key={i} className="border-t" style={{ borderColor: '#E2DFD8' }}>
                          <td className="p-2">{p.product_name}</td>
                          <td className="p-2 text-right font-medium" style={{ color: '#C05C35' }}>{p.in_transit_qty}</td>
                          <td className="p-2 font-mono text-[10px]" style={{ color: '#5C6B61' }}>{(p.dispatch_ids || []).join(', ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}

              {auditData.sample_in_transit_dispatches && auditData.sample_in_transit_dispatches.length > 0 && (
                <section>
                  <h4 className="font-semibold mb-1" style={{ color: '#2B5339' }}>Sample in-transit dispatches (first 30)</h4>
                  <table className="w-full text-xs border">
                    <thead style={{ background: '#FBF9F6' }}>
                      <tr>
                        <th className="p-2 text-left">Order</th>
                        <th className="p-2 text-left">Dealer</th>
                        <th className="p-2 text-left">Status</th>
                        <th className="p-2 text-left">Sched</th>
                        <th className="p-2 text-left">Vehicle</th>
                        <th className="p-2 text-left">Dispatched</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditData.sample_in_transit_dispatches.map((d, i) => (
                        <tr key={i} className="border-t" style={{ borderColor: '#E2DFD8' }}>
                          <td className="p-2 font-mono text-[10px]">{(d.order_id || '').slice(0, 8)}</td>
                          <td className="p-2">{d.dealer_name}</td>
                          <td className="p-2 uppercase">{d.order_status}</td>
                          <td className="p-2">{d.delivery_date || '-'}</td>
                          <td className="p-2">{d.vehicle_number || '-'}</td>
                          <td className="p-2" style={{ color: '#5C6B61' }}>{d.dispatched_at ? String(d.dispatched_at).slice(0, 10) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}

              {auditData.stock_in_transit_dispatch_count === 0 && (
                <p className="text-sm font-medium" style={{ color: '#2B5339' }}>✓ No facility-less in-transit dispatches — Stock's in_transit column should be 0 across all products.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="stock-history-dialog">
          <DialogHeader>
            <DialogTitle>Stock Adjustment History</DialogTitle>
          </DialogHeader>
          {historyLoading ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-6" /> : (
            <div>
              {history.length === 0 ? (
                <p className="text-sm" style={{ color: '#5C6B61' }}>No adjustments yet.</p>
              ) : (
                <table className="w-full text-xs border">
                  <thead style={{ background: '#FBF9F6' }}>
                    <tr>
                      <th className="p-2 text-left">When</th>
                      <th className="p-2 text-left">Facility</th>
                      <th className="p-2 text-left">Product</th>
                      <th className="p-2 text-right">Δ</th>
                      <th className="p-2 text-right">New Qty</th>
                      <th className="p-2 text-left">Reason</th>
                      <th className="p-2 text-left">By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(h => (
                      <tr key={h.id} className="border-t" style={{ borderColor: '#E2DFD8' }}>
                        <td className="p-2" style={{ color: '#5C6B61' }}>{fmtDateTime(h.created_at)}</td>
                        <td className="p-2">{h.facility}</td>
                        <td className="p-2">{h.product_name}</td>
                        <td className="p-2 text-right font-medium" style={{ color: h.delta < 0 ? '#C05C35' : '#2B5339' }}>{h.delta > 0 ? `+${h.delta}` : h.delta}</td>
                        <td className="p-2 text-right">{(h.resulting_qty || 0).toLocaleString('en-IN')}</td>
                        <td className="p-2" style={{ color: '#5C6B61' }}>{h.reason || '-'}</td>
                        <td className="p-2" style={{ color: '#5C6B61' }}>{h.created_by || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Order Review Dialog — opened from Dispatch popover's Order # click.
          Read-only view (no edit/dispatch buttons) since this is the Stock
          context, not Orders. */}
      <Dialog open={!!reviewOrderId} onOpenChange={(o) => { if (!o) setReviewOrderId(null); }}>
        <DialogContent className="max-w-2xl" data-testid="stock-order-review-dialog">
          <DialogHeader>
            <DialogTitle>Order Review — #{(reviewOrderId || '').slice(0, 8)}</DialogTitle>
          </DialogHeader>
          {reviewOrder ? (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded" style={{ background: '#FBF9F6' }}>
                  <div className="text-xs" style={{ color: '#5C6B61' }}>Dealer</div>
                  <div className="font-medium">{reviewOrder.dealer_name || '-'}</div>
                  <div className="text-xs" style={{ color: '#5C6B61' }}>{reviewOrder.dealer_phone || ''}</div>
                </div>
                <div className="p-3 rounded" style={{ background: '#FBF9F6' }}>
                  <div className="text-xs" style={{ color: '#5C6B61' }}>Status</div>
                  <div className="font-medium uppercase">{reviewOrder.status || '-'}</div>
                  {reviewOrder.delivery_date && (
                    <div className="text-xs" style={{ color: '#5C6B61' }}>Schedule: {reviewOrder.delivery_date} {reviewOrder.delivery_time || ''}</div>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold mb-1.5" style={{ color: '#5C6B61' }}>Items</div>
                <div className="overflow-auto rounded" style={{ border: '1px solid #E2DFD8' }}>
                  <table className="w-full text-xs">
                    <thead style={{ background: '#FBF9F6', color: '#5C6B61' }}>
                      <tr>
                        <th className="text-left px-2 py-1">Product</th>
                        <th className="text-right px-2 py-1">Qty</th>
                        <th className="text-right px-2 py-1">Price</th>
                        <th className="text-right px-2 py-1">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(reviewOrder.items || []).map((it, idx) => (
                        <tr key={idx} className="border-t" style={{ borderColor: '#E2DFD8' }}>
                          <td className="px-2 py-1">{it.product_name || '-'}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{Number(it.quantity || 0).toLocaleString('en-IN')}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{'\u20B9'}{Number(it.price || 0).toLocaleString('en-IN')}</td>
                          <td className="px-2 py-1 text-right tabular-nums font-semibold">{'\u20B9'}{Number(it.total || (it.quantity || 0) * (it.price || 0)).toLocaleString('en-IN')}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: '#FBF9F6' }}>
                        <td className="px-2 py-1 font-semibold" colSpan={3}>Bill Amount</td>
                        <td className="px-2 py-1 text-right font-bold tabular-nums" style={{ color: '#2B5339' }}>
                          {'\u20B9'}{Number(reviewOrder.total_amount || reviewOrder.total || (reviewOrder.items || []).reduce((s, i) => s + Number(i.total || (i.quantity || 0) * (i.price || 0)), 0)).toLocaleString('en-IN')}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              {(reviewOrder.dispatches || []).length > 0 && (
                <div>
                  <div className="text-xs font-semibold mb-1.5" style={{ color: '#5C6B61' }}>Dispatches ({reviewOrder.dispatches.length})</div>
                  <div className="space-y-1.5">
                    {reviewOrder.dispatches.map((d, idx) => (
                      <div key={d.id || idx} className="p-2 rounded text-xs" style={{ background: '#FBF9F6', border: '1px solid #E2DFD8' }}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-mono" style={{ color: '#5C6B61' }}>#{(d.id || '').slice(0, 8)}</span>
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: d.delivered_at ? '#2B5339' : '#C05C35', color: 'white' }}>
                            {d.delivered_at ? 'DELIVERED' : 'IN TRANSIT'}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-1" style={{ color: '#5C6B61' }}>
                          <div>Dispatched: {d.dispatched_at ? new Date(d.dispatched_at).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}</div>
                          {d.delivered_at && <div>Delivered: {new Date(d.delivered_at).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>}
                          <div>Vehicle: {d.vehicle_number || '-'}</div>
                          <div>Driver: {d.driver_mobile || '-'}</div>
                          <div>Facility: {d.facility || '(split at delivery)'}</div>
                          <div>Items: {(d.items || []).reduce((s, i) => s + (i.quantity || 0), 0)} bags</div>
                        </div>
                        {(d.delivery_splits || []).length > 0 && (
                          <div className="mt-1 pt-1 border-t text-[10px]" style={{ borderColor: '#E2DFD8', color: '#5C6B61' }}>
                            Splits: {d.delivery_splits.map((s) => `${s.facility}:${s.quantity}`).join(' · ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-center py-6" style={{ color: '#5C6B61' }}>
              Order not found in current data. It may have been deleted, or try refreshing the page.
            </div>
          )}
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
};

